<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="view-button" class="custom-btn-secondary mr-1" @click="toggleView">
          <font-awesome-icon :icon="viewMode === 'table' ? 'th-large' : 'list'"/>
        </b-button>
        <b-tooltip target="view-button" triggers="hover" custom-class="custom-tooltip" noninteractive>
          {{ viewMode === 'table' ? 'Канбан' : 'Таблица' }}
        </b-tooltip>
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-btn v-if="canEditRecruitJobPostings()" class="custom-btn-primary" :disabled="isBusy" @click="showModal(null, false)">
          <v-icon name="buttons/plus" :size="12"/>
          Новое объявление
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :hide-search="true"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12">
            <label class="small text-muted">Вакансии</label>
            <vacancy-multi-select
              id="vacancy-multi-select"
              ref="vacancy_multi_select"
              @onSelect="onSelectVacancy"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Дата публикации</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input-start-opened-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_opened_date"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input-end-opened-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_opened_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Дата первого выхода в смену</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input-start-closed-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_closed_date"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input-end-closed-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_closed_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Объекты</label>
            <location-multi-select
              id="location-multi-select"
              ref="location_multi_select"
              @onSelect="onSelectLocation"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Ответственные</label>
            <user-multi-select
              id="user-multi-select-assigned"
              ref="user_multi_select_assigned"
              title="Ответственные"
              @onSelect="onSelectUserAssigned"
            />
          </b-col>
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">Статус</label>
            <b-form-select
              id="input-statuses"
              size="sm"
              v-model="filters.status"
              :options="statusOptions"
            />
          </b-col>
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">Сервис</label>
            <b-form-select
              id="input-provider-id"
              size="sm"
              v-model="filters.provider_id"
              :options="providerOptions"
            />
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container" :class="{ 'border-0': viewMode === 'kanban' }">
          <b-table
            v-if="viewMode === 'table'"
            id="recruit-job-postings"
            ref="recruit_job_postings"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-dblclicked="onRowClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="4"/>
            </template>

            <template #cell(assigned)="row">
              {{ getAssignedName(row.item.assigned) }}
            </template>

            <template #cell(status_id)="row">
              {{ row.item.status_name || getStatusText(row.item.status_id) }}
            </template>

            <template #cell(opened_date)="row">
              {{formatTableDate(row.item.opened_date)}}
            </template>

            <template #cell(action)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" no-caret class="custom-dropdown">
                <template #button-content>
                  <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                </template>
                <b-dropdown-item variant="success" @click.stop="showModal(row.item.id, true)">
                  <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                  Смотреть
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditRecruitJobPostings()" variant="primary" @click="showModal(row.item.id, false)">
                  <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                  Редактировать
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditRecruitJobPostings()" variant="danger" @click="onRemove(row)">
                  <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                  Удалить
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <kanban
            v-if="viewMode === 'kanban'"
            :items="items"
            :columns="kanbanColumns"
            group-name="recruit-job-postings"
            @status-updated="kanbanUpdateStatus"
          >
            <template #card="{ item }">
              <b-card
                no-body
                class="kb-card shadow-sm"
                @dblclick="onRowClicked(item)"
              >
                <div class="d-flex justify-content-between align-items-center w-100">
                  <div class="font-weight-bold text-truncate mr-2">
                    {{ item.location_vacancy_name }}
                  </div>
                  <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none flex-shrink-0" no-caret class="custom-dropdown p-0 m-0 custom-btn-secondary" style="width: 16px !important;">
                    <template #button-content>
                      <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                    </template>
                    <b-dropdown-item variant="success" @click.stop="showModal(item.id, true)">
                      <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                      Смотреть
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canEditRecruitJobPostings()" variant="primary" @click="showModal(item.id, false)">
                      <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                      Редактировать
                    </b-dropdown-item>
                    <b-dropdown-item v-if="canEditRecruitJobPostings()" variant="danger" @click="onRemove({ item })">
                      <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                      Удалить
                    </b-dropdown-item>
                  </b-dropdown>
                </div>

                <div class="d-flex align-items-center w-100 small text-truncate">
                  <v-icon name="navbar/locations" :size="13" class="mr-2 flex-shrink-0"/>
                  <span class="text-truncate">{{ item.location_name }}</span>
                </div>

                <div class="d-flex align-items-center w-100 small">
                  <v-icon name="buttons/calendar" :size="13" class="mr-2 flex-shrink-0"/>
                  <span>{{ formatTableDate(item.opened_date) }}</span>
                </div>

                <div v-if="item.provider_name" class="d-flex align-items-center w-100 small text-truncate">
                  <v-icon name="navbar/locations" :size="13" class="mr-2 flex-shrink-0"/>
                  <span class="text-truncate">{{ item.provider_name }}</span>
                </div>

                <hr class="w-100 m-0 border-light"/>

                <div class="d-flex justify-content-between align-items-center w-100 small text-muted">
                  <b-button variant="link" style="color: #1A9167 !important;" class="p-0 pl-1 mr-2 text-decoration-none" @click="onRowClicked(item)">
                    Открыть
                  </b-button>
                  <div class="text-truncate">
                    <font-awesome-icon icon="user" class="mr-1"/>
                    {{ getAssignedName(item.assigned) || 'Не назначен' }}
                  </div>
                </div>
              </b-card>
            </template>
          </kanban>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          />
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>Нет созданных объявлений.</h5>
        <p>Создайте новое объявление прямо сейчас</p>
        <b-btn v-if="canEditRecruitJobPostings()" class="custom-btn-primary mt-1" @click="showModal(null, false)">
          <v-icon name="buttons/plus" :size="12" class="mr-1"/>
          Новое объявление
        </b-btn>
      </div>
    </div>

    <DynamicTables
      storage-key="recruitJobPostings_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />

    <RecruitJobPosting ref="recruit_job_posting" @refresh="getItems" @modal-closed="onModalClosed"/>
  </div>
</template>

<script>
import recruitJobPostings from "../../network/recruitJobPostings";
import catalogs from "../../network/catalogs";
import users from "../../network/users";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import Kanban from "../../utilites/commonUi/Kanban.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
  import draggable from 'vuedraggable';
    import LocationMultiSelect from "../Locations/LocationMultiSelect.vue";
    import UserMultiSelect from "../Users/UserMultiSelect.vue";
    import VacancyMultiSelect from "../Catalogs/Vacancies/VacancyMultiSelect.vue";
    import RecruitJobPosting from "./RecruitJobPosting.vue";

export default {
  name: "RecruitJobPostings",
  components: { TablesSkeleton, PaginationFooter, Kanban, FiltersDrawer, RecruitJobPosting, draggable, UserMultiSelect, LocationMultiSelect, DynamicTables, VacancyMultiSelect },
  mixins: [
    recruitJobPostings, catalogs, users,
    helpers, dateFormatter, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Объявления',
      sortBy: 'id',
      sortDesc: true,
      items: [],
      usersList: [],
      vacanciesList: [],
      fields: [
        {
          key: 'recruit_task_id',
          label: 'ID заявки',
          sortable: true,
          visible: true,
          class: 'recruitJobPostingsId'
        },
        {
          key: 'id',
          label: 'ID объявления',
          sortable: true,
          visible: true,
        },
        {
          key: 'provider_name',
          label: 'Сервис',
          sortable: true,
          visible: true,
        },
        {
          key: 'assigned',
          label: 'Ответственный',
          sortable: true,
          visible: true,
        },
        {
          key: 'status_id',
          label: 'Статус',
          sortable: true,
          visible: true,
        },
        {
          key: 'opened_date',
          label: 'Дата публикации',
          sortable: true,
          visible: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: 'Действия',
        class: 'recruitJobPostingsAction',
      },
      isBusy: false,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
      },
      statusOptions: [],
      providerOptions: [
        { value: null, text: 'Все сервисы' },
        { value: 1, text: 'Авито' },
        { value: 2, text: 'HH' },
      ],
      viewMode: 'table',
      kanbanColumns: [
        { id: 'created', title: 'Создано', class: 'tag-secondary', items: [] },
        { id: 'in_progress', title: 'В работе', class: 'tag-primary', items: [] },
        { id: 'published', title: 'Опубликовано', class: 'tag-info', items: [] },
        { id: 'done', title: 'Готово', class: 'tag-success', items: [] },
        { id: 'canceled', title: 'Отменено', class: 'tag-danger', items: [] }
      ],
    }
  },
  async mounted() {
    await this.configureRefs();
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.start_opened_date) {
        this.filters.start_opened_date = query.start_opened_date;
      }

      if (query.end_opened_date) {
        this.filters.end_opened_date = query.end_opened_date;
      }

      if (query.start_closed_date) {
        this.filters.start_closed_date = query.start_closed_date;
      }

      if (query.end_closed_date) {
        this.filters.end_closed_date = query.end_closed_date;
      }

      if (query.locations) {
          const decodeLoc = decodeURIComponent(query.locations);
          this.filters.locations = decodeLoc.split(",");
      }

      if (query.assigned) {
        const decodeLoc = decodeURIComponent(query.assigned);
        this.filters.assigned = decodeLoc.split(",");
      }

      if (query.vacancies) {
        const decodeLoc = decodeURIComponent(query.vacancies);
        this.filters.vacancies = decodeLoc.split(",");
      }

      if (query.status) {
        this.filters.status = query.status;
      }

      if (query.provider_id) {
        this.filters.provider_id = query.provider_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.vacanciesList = (await this.getVacancies({})).data.items;
    this.usersList = (await this.getUsersSearch({})).data.items;
    const status = (await this.getJobPostingStatuses({})).data.items;

    this.statusOptions = status.map(s => ({
      value: s.id,
      text: s.active ? s.name : `${s.name} (неактивен)`,
    }));

    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [...this.visibleFields, this.serviceEndField];
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.start_opened_date || this.filters.end_opened_date) {
        tags.push({ key: 'opened_date', label: 'Дата публикации' });
      }

      if (this.filters.start_closed_date || this.filters.end_closed_date) {
        tags.push({ key: 'closed_date', label: 'Дата выхода в смену' });
      }

      if (this.filters.locations.length > 0) {
        tags.push({ key: 'location', label: `Объекты: ${this.filters.locations.length}`});
      }

      if (this.filters.status !== defaults.status) {
        const foundItem = this.statusOptions.find(s => s.value === this.filters.status);
        tags.push({ key: 'status', label: foundItem ? foundItem.text : 'Статус заявки' });
      }

      if (this.filters.assigned.length > 0) {
        tags.push({ key: 'assigned', label: `Ответственные: ${this.filters.assigned.length}`,});
      }

      if (this.filters.vacancies.length > 0) {
        tags.push({ key: 'vacancy', label: `Вакансии: ${this.filters.vacancies.length}`,});
      }

      if (this.filters.provider_id !== defaults.provider_id) {
        const foundItem = this.providerOptions.find(s => s.value === this.filters.provider_id);
        tags.push({ key: 'provider_id', label: foundItem ? foundItem.text : 'Сервис' });
      }

      return tags;
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        start_opened_date: this.filters.start_opened_date,
        end_opened_date: this.filters.end_opened_date,
        start_closed_date: this.filters.start_closed_date,
        end_closed_date: this.filters.end_closed_date,
        locations: this.filters.locations,
        status: this.filters.status,
        assigned: this.filters.assigned,
        vacancies: this.filters.vacancies,
        provider_id: this.filters.provider_id,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getJobPostings(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
    },
    showModal(jobPostingId, isViewonly) {
      const canEdit = this.canEditRecruitJobPostings();

      if (!jobPostingId) {
        if (!canEdit) return;

        this.$refs.recruit_job_posting.showModal(null, false);
        return;
      }

      const isView = isViewonly || !canEdit;
      const query = isView ? { mode: 'view' } : {};

      this.$router.push({
        name: 'RecruitJobPostings',
        params: { id: jobPostingId },
        query: query
      });
    },
    onRowClicked(item) {
      this.showModal(item.id, false);
    },
    onModalClosed() {
      if (this.$route.params.id) {
        this.$router.replace({
          name: 'RecruitJobPostings',
          params: { id: null },
          hash: this.$route.hash,
        }).catch(()=>{});
      }
    },
    getAssignedName(userId) {
      if (!userId) return null;

      const user = this.usersList.find(u => u.id === userId);
      if (!user) { return '' }
      return user.name;
    },
    getStatusText(statusValue) {
      const option = this.statusOptions.find(opt => opt.value === statusValue);
      return option ? option.text : statusValue;
    },
    onRemove(row) {
      if (!this.canEditRecruitJobPostings()) return;

      if (row.item) {
        this.deleteRow = row.item
      }

      const title = "Удаление объявления";
      const hasAvitoPublication = Number(this.deleteRow.provider_id) === 1 &&
        (this.deleteRow.provider_post_id || this.deleteRow.provider_post_uuid);
      const message = hasAvitoPublication
        ? `Вы действительно хотите удалить объявление № "${this.deleteRow.id}"? Оно также будет снято с публикации на Авито.`
        : `Вы действительно хотите удалить объявление № "${this.deleteRow.id}"?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteJobPosting(this.deleteRow);

        if (result.error) {
          this.showToast(result.error.message || 'Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Объявление удалено.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        start_opened_date: null,
        end_opened_date: null,
        start_closed_date: null,
        end_closed_date: null,
        locations: [],
        status: null,
        assigned: [],
        vacancies: [],
        provider_id: null,
      };
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;
      await this.configureRefs();
      await this.onSearch();
    },
    removeFilterTag(tag) {
      if (tag.key === 'opened_date') {
        this.filters.start_opened_date = null;
        this.filters.end_opened_date = null;
      }
      else if (tag.key === 'closed_date') {
        this.filters.start_closed_date = null;
        this.filters.end_closed_date = null;
      }
      else if (tag.key === 'location') {
        this.filters.locations = [];
        this.$refs.location_multi_select.onClearSelected();
      }
      else if (tag.key === 'status') {
        this.filters.status = null;
      }
      else if (tag.key === 'assigned') {
        this.filters.assigned = [];
        this.$refs.user_multi_select_assigned.onClearSelected();
      }
      else if (tag.key === 'vacancy') {
        this.filters.vacancies = [];
        this.$refs.vacancy_multi_select.onClearSelected();
      }
      else if (tag.key === 'provider_id') {
        this.filters.provider_id = null;
      }

      this.onSearch();
    },
    async configureRefs() {
      if (this.$refs.user_multi_select_assigned != null) {
        await this.$refs.user_multi_select_assigned.configure(this.filters.assigned);
      }

      if (this.$refs.location_multi_select != null) {
        await this.$refs.location_multi_select.configure(this.filters.locations);
      }

      if (this.$refs.vacancy_multi_select != null) {
        await this.$refs.vacancy_multi_select.configure(this.filters.vacancies);
      }
    },
    onSelectLocation(locations) {
      this.filters.locations = locations.map(item => { return item.id; });
    },
    onSelectUserAssigned(users) {
      this.filters.assigned = users.map(item => { return item.id; });
    },
    onSelectVacancy(vacancies) {
      this.filters.vacancies = vacancies.map(item => { return item.id; });
    },
    // Kanban
    toggleView() {
      this.viewMode = this.viewMode === 'table' ? 'kanban' : 'table';
    },
    async kanbanUpdateStatus({ item, newStatus, oldStatus }) {
      const jobPosting = this.items.find(t => t.id === item.id);
      jobPosting.status = newStatus;

      const result = await this.putJobPosting(jobPosting);

      if (result.error || !result.data) {
        jobPosting.status = oldStatus;
        this.showError('Не удалось обновить статус.');
        await this.getItems();
      } else {
        this.showToast('Статус объявления обновлён.', 'Успех', 'success');
      }
    },
    // Permissions
    canEditRecruitJobPostings() {
      return this.hasPermission([PERM.RECRUIT_JOB_POSTING_EDIT]);
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        this.$nextTick(() => {
          if (newId && this.$refs.recruit_job_posting) {
            const isViewonly = this.$route.query.mode === 'view';
            this.$refs.recruit_job_posting.showModal(newId, isViewonly);
          } else if (!newId && this.$refs.recruit_job_posting) {
            this.$refs.recruit_job_posting.hideModal();
          }
        });
      }, immediate: true
    },
  }
}
</script>

<style>
.recruitJobPostingsId {
  padding-left: 10px !important;
  width: 100px;
  text-align: center;
}

.recruitJobPostingsAction {
  width: 80px;
  padding-right: 10px !important;
  text-align: center;
}

#recruit-job-postings td {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/Recruiting/RecruitJobPostings.vue